import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createWorkout,
  deleteWorkout,
  getWorkoutKpis,
  getWorkouts,
  updateWorkout,
  DeleteImpact,
  WorkoutFilters,
  WorkoutPayload,
} from '@/api/workoutsService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const PAGE_SIZE = 30;

export const extractDeleteImpact = (error: unknown): DeleteImpact | null => {
  const res = (error as { response?: { data?: { impact?: DeleteImpact } } })?.response;
  return res?.data?.impact ?? null;
};

export const useWorkouts = (filters: { search?: string; themeIds?: string[]; sportIds?: string[]; testsOnly?: boolean; page: number }) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'workouts'] });

  const listFilters: WorkoutFilters = {
    search: filters.search,
    themeIds: filters.themeIds,
    sportIds: filters.sportIds,
    testsOnly: filters.testsOnly,
    page: filters.page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.pv2Workouts(filters),
    queryFn: () => getWorkouts(listFilters),
  });

  const { data: kpis } = useQuery({
    queryKey: queryKeys.pv2WorkoutKpis,
    queryFn: getWorkoutKpis,
  });

  const onWriteSuccess = (title: string) => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: queryKeys.pv2WorkoutKpis });
    toaster.create({ title, type: 'success' });
  };

  const createMutation = useMutation({
    mutationFn: (payload: WorkoutPayload) => createWorkout(payload),
    onSuccess: () => onWriteSuccess('Séance créée'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: WorkoutPayload }) => updateWorkout(id, payload),
    onSuccess: (workout) => {
      onWriteSuccess('Séance mise à jour');
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2Workout(workout.id) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWorkout(id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2WorkoutKpis });
      toaster.create({ title: 'Séance supprimée', type: 'success' });
    },
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize: PAGE_SIZE,
    kpis,
    isLoading,
    createWorkout: createMutation.mutateAsync,
    updateWorkout: updateMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
    deleteWorkout: deleteMutation.mutateAsync,
    invalidate,
  };
};
